"use client";
import { useState } from "react";

import PageShell from "@/components/PageShell";
type FormState = {
  name: string;
  email: string;
  phone: string;
  address: string;
  topic: string;
  message: string;
};

type Status = "idle" | "loading" | "success" | "error";

const INITIAL: FormState = { name: "", email: "", phone: "", address: "", topic: "", message: "" };

export default function ContactPage() {
  const [form, setForm] = useState<FormState>(INITIAL);
  const [errors, setErrors] = useState<Partial<FormState>>({});
  const [status, setStatus] = useState<Status>("idle");

  function validate(): boolean {
    const e: Partial<FormState> = {};
    if (!form.name.trim()) e.name = "Name is required";
    if (!form.email.trim()) e.email = "Email is required";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = "Enter a valid email";
    if (form.phone.trim() && !/^[+\d][\d\s()-]{6,19}$/.test(form.phone.trim()))
      e.phone = "Enter a valid mobile number";
    if (!form.topic) e.topic = "Please select a topic";
    if (!form.message.trim()) e.message = "Message is required";
    else if (form.message.trim().length < 10) e.message = "Message is too short";
    setErrors(e);
    return Object.keys(e).length === 0;
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!validate()) return;
    setStatus("loading");
    try {
      const res = await fetch("/api/contact", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      if (!res.ok) throw new Error("Server error");
      setStatus("success");
      setForm(INITIAL);
      setErrors({});
    } catch {
      setStatus("error");
    }
  }

  function handleChange(field: keyof FormState) {
    return (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
      setForm(prev => ({ ...prev, [field]: e.target.value }));
      if (errors[field]) setErrors(prev => ({ ...prev, [field]: undefined }));
    };
  }

  const inputBase: React.CSSProperties = {
    padding: "12px 16px",
    border: "1px solid var(--b200)",
    borderRadius: "4px",
    fontSize: "14px",
    background: "var(--b50)",
    outline: "none",
    width: "100%",
    fontFamily: "var(--font-dm), sans-serif",
    color: "var(--o900)",
    transition: "border-color .18s",
  };

  const inputError: React.CSSProperties = { ...inputBase, borderColor: "var(--red)" };
  const errText: React.CSSProperties = { fontSize: "12px", color: "var(--red)", marginTop: "4px" };

  return (
    <><PageShell>
      <section className="simple-hero" style={{ padding: "72px clamp(20px,4vw,52px) 60px", borderBottom: "1px solid var(--b200)" }}>
        <div className="ey rev">Contact</div>
        <h1 className="sh2 rev d1" style={{ fontSize: "clamp(36px,4.5vw,58px)" }}>Get in <em>touch.</em></h1>
      </section>

      <section style={{ padding: "44px clamp(20px,4vw,52px)", borderBottom: "1px solid var(--b200)" }}>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "52px" }}>

          {/* Form */}
          <div>
            <h2 style={{ fontFamily: "var(--font-fraunces), serif", fontSize: "22px", fontWeight: 200, color: "var(--o900)", marginBottom: "24px" }}>
              Send us a message
            </h2>

            {status === "success" ? (
              <div style={{ padding: "24px", background: "rgba(22,163,74,.06)", border: "1px solid rgba(22,163,74,.2)", borderRadius: "6px" }}>
                <div style={{ fontSize: "15px", color: "var(--green)", fontWeight: 500, marginBottom: "6px" }}>Message sent ✓</div>
                <p style={{ fontSize: "14px", color: "rgba(19,18,16,.55)", lineHeight: 1.7 }}>
                  We&apos;ll get back to you within 24 hours at <strong>{form.email || "your email"}</strong>.
                </p>
                <button
                  style={{ marginTop: "16px", fontSize: "13px", color: "var(--cu)", background: "none", border: "none", cursor: "pointer", padding: 0 }}
                  onClick={() => setStatus("idle")}
                >
                  Send another message →
                </button>
              </div>
            ) : (
              <form noValidate onSubmit={handleSubmit}>
                <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>

                  <div>
                    <input
                      aria-label="Your name"
                      placeholder="Your name"
                      style={errors.name ? inputError : inputBase}
                      type="text"
                      value={form.name}
                      onChange={handleChange("name")}
                    />
                    {errors.name && <p style={errText}>{errors.name}</p>}
                  </div>

                  <div>
                    <input
                      aria-label="Email address"
                      placeholder="Email address"
                      style={errors.email ? inputError : inputBase}
                      type="email"
                      value={form.email}
                      onChange={handleChange("email")}
                    />
                    {errors.email && <p style={errText}>{errors.email}</p>}
                  </div>

                  <div>
                    <input
                      aria-label="Mobile number"
                      autoComplete="tel"
                      inputMode="tel"
                      placeholder="Mobile number (optional)"
                      style={errors.phone ? inputError : inputBase}
                      type="tel"
                      value={form.phone}
                      onChange={handleChange("phone")}
                    />
                    {errors.phone && <p style={errText}>{errors.phone}</p>}
                  </div>

                  <div>
                    <input
                      aria-label="City or address"
                      autoComplete="street-address"
                      placeholder="City / address (optional)"
                      style={inputBase}
                      type="text"
                      value={form.address}
                      onChange={handleChange("address")}
                    />
                  </div>

                  <div>
                    <select
                      aria-label="Select topic"
                      style={{ ...(errors.topic ? inputError : inputBase), color: form.topic ? "var(--o900)" : "rgba(19,18,16,.45)" }}
                      value={form.topic}
                      onChange={handleChange("topic")}
                    >
                      <option value="">Select topic</option>
                      <option value="Sales inquiry">Sales inquiry</option>
                      <option value="Enterprise plan">Enterprise plan</option>
                      <option value="Technical support">Technical support</option>
                      <option value="Partnership">Partnership</option>
                      <option value="Press inquiry">Press inquiry</option>
                      <option value="Other">Other</option>
                    </select>
                    {errors.topic && <p style={errText}>{errors.topic}</p>}
                  </div>

                  <div>
                    <textarea
                      aria-label="Your message"
                      placeholder="Your message"
                      rows={5}
                      style={{ ...(errors.message ? inputError : inputBase), resize: "vertical" }}
                      value={form.message}
                      onChange={handleChange("message")}
                    />
                    {errors.message && <p style={errText}>{errors.message}</p>}
                  </div>

                  {status === "error" && (
                    <p style={{ fontSize: "13px", color: "var(--red)", padding: "10px 14px", background: "rgba(220,38,38,.05)", borderRadius: "4px", border: "1px solid rgba(220,38,38,.15)" }}>
                      Something went wrong. Please try again or email us at{" "}
                      <a href="mailto:info@globalsynapsetech.com" style={{ color: "var(--red)" }}>info@globalsynapsetech.com</a>.
                    </p>
                  )}

                  <button
                    className="btn-p"
                    disabled={status === "loading"}
                    style={{ alignSelf: "flex-start", padding: "12px 28px", border: "none", cursor: status === "loading" ? "not-allowed" : "pointer", fontSize: "14px", opacity: status === "loading" ? 0.7 : 1 }}
                    type="submit"
                  >
                    <span>{status === "loading" ? "Sending…" : "Send message →"}</span>
                  </button>

                </div>
              </form>
            )}
          </div>

          {/* Contact info */}
          <div>
            <h2 style={{ fontFamily: "var(--font-fraunces), serif", fontSize: "22px", fontWeight: 200, color: "var(--o900)", marginBottom: "24px" }}>
              Other ways to reach us
            </h2>
            {([
              { l: "General", v: "info@globalsynapsetech.com", h: "mailto:info@globalsynapsetech.com", icon: "mail" },
              { l: "Support", v: "support@globalsynapsetech.com", h: "mailto:support@globalsynapsetech.com", icon: "support" },
              { l: "Phone", v: "+91 92057 57755", h: "tel:+919205757755", icon: "phone" },
              { l: "WhatsApp", v: "+91 92057 57755", h: "https://wa.me/919205757755", icon: "whatsapp" },
              { l: "Office", v: "Delhi, India", h: null, icon: "pin" },
              { l: "Response time", v: "Within 24 hours", h: null, icon: "clock" },
            ] as const).map((c, i) => (
              <div
                key={i}
                style={{
                  padding: "16px 0",
                  borderBottom: "1px solid var(--b200)",
                  display: "flex",
                  alignItems: "center",
                  gap: "14px",
                }}
              >
                <span
                  aria-hidden="true"
                  style={{
                    flexShrink: 0,
                    width: 36,
                    height: 36,
                    borderRadius: "50%",
                    background: "var(--b100)",
                    border: "1px solid var(--b200)",
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: "var(--cu)",
                  }}
                >
                  <ContactIcon kind={c.icon} />
                </span>
                <div style={{ minWidth: 0, flex: 1 }}>
                  <div style={{ fontSize: "9px", letterSpacing: ".14em", textTransform: "uppercase", color: "rgba(19,18,16,.35)", marginBottom: "4px" }}>{c.l}</div>
                  {c.h ? (
                    <a
                      href={c.h}
                      {...(c.icon === "whatsapp" ? { target: "_blank", rel: "noopener noreferrer" } : {})}
                      style={{ fontSize: "15px", color: "var(--o900)", textDecoration: "none", wordBreak: "break-all" }}
                    >
                      {c.v}
                    </a>
                  ) : (
                    <span style={{ fontSize: "15px", color: "var(--o900)" }}>{c.v}</span>
                  )}
                </div>
              </div>
            ))}
          </div>

        </div>
      </section>
    </PageShell></>
  );
}

// Inline SVG icons for contact channels. Stroke-only line style to match the
// site's restrained visual language; inherits color from `currentColor` so the
// orange copper accent flows through.
function ContactIcon({ kind }: { kind: "mail" | "support" | "phone" | "whatsapp" | "pin" | "clock" }) {
  const common = { width: 18, height: 18, viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: 1.6, strokeLinecap: "round" as const, strokeLinejoin: "round" as const };
  switch (kind) {
    case "mail":
      return (
        <svg {...common}>
          <rect height="14" rx="2" width="18" x="3" y="5" />
          <path d="m3 7 9 6 9-6" />
        </svg>
      );
    case "support":
      return (
        <svg {...common}>
          <path d="M4 12a8 8 0 0 1 16 0v5a2 2 0 0 1-2 2h-1v-7h3" />
          <path d="M4 12v5a2 2 0 0 0 2 2h1v-7H4" />
        </svg>
      );
    case "phone":
      return (
        <svg {...common}>
          <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6A19.79 19.79 0 0 1 2.12 4.18 2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.13.96.36 1.9.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.91.34 1.85.57 2.81.7A2 2 0 0 1 22 16.92z" />
        </svg>
      );
    case "whatsapp":
      // WhatsApp's official logo glyph — recognisable, filled for brand match.
      return (
        <svg fill="currentColor" height="18" viewBox="0 0 24 24" width="18">
          <path d="M17.6 14.2c-.3-.15-1.77-.87-2.04-.97-.27-.1-.47-.15-.67.15-.2.3-.77.97-.94 1.17-.17.2-.35.22-.65.07-.3-.15-1.26-.47-2.4-1.49-.89-.79-1.49-1.77-1.66-2.07-.17-.3-.02-.46.13-.61.13-.13.3-.35.45-.52.15-.17.2-.3.3-.5.1-.2.05-.37-.02-.52-.07-.15-.67-1.62-.92-2.21-.24-.58-.49-.5-.67-.51l-.57-.01c-.2 0-.52.07-.79.37-.27.3-1.04 1.02-1.04 2.49 0 1.47 1.07 2.89 1.22 3.09.15.2 2.1 3.21 5.09 4.5.71.31 1.27.49 1.7.63.71.23 1.36.2 1.88.12.57-.08 1.77-.72 2.02-1.42.25-.7.25-1.29.17-1.42-.07-.13-.27-.2-.57-.35zM12.05 21.5c-1.69 0-3.36-.45-4.83-1.31l-.34-.2-3.59.94.96-3.5-.22-.36a9.43 9.43 0 0 1-1.45-5.05c0-5.22 4.25-9.46 9.48-9.46 2.53 0 4.91.99 6.7 2.78a9.41 9.41 0 0 1 2.78 6.69c-.01 5.21-4.25 9.47-9.47 9.47zm8.06-17.52A11.4 11.4 0 0 0 12.05 1c-6.3 0-11.43 5.12-11.43 11.43 0 2.01.53 3.98 1.53 5.71L1 23l5-1.31a11.4 11.4 0 0 0 5.45 1.39h.01c6.3 0 11.43-5.12 11.43-11.43a11.36 11.36 0 0 0-3.78-8.45z" />
        </svg>
      );
    case "pin":
      return (
        <svg {...common}>
          <path d="M12 21s-7-7.16-7-12a7 7 0 1 1 14 0c0 4.84-7 12-7 12z" />
          <circle cx="12" cy="9" r="2.5" />
        </svg>
      );
    case "clock":
      return (
        <svg {...common}>
          <circle cx="12" cy="12" r="9" />
          <path d="M12 7v5l3 2" />
        </svg>
      );
  }
}
